/**
 * @fileoverview The words for one photo of a batch that did not go onto the tray (useTrayBatch): the sentence for a
 * failure on our side (reported to Sentry as it is chosen; for the server's request limit, its own calm sentence), or
 * a refusal's own words. Never both for one failure: the request limit's 429 is "ours" and also a refusal with words,
 * and Check and save showed its sentence twice (B104, 2026-10-28).
 *
 * Where: useTrayBatch (Check and save, the Photos page's "Choose photos").
 * Words: none of its own (serverFailureText, serverMessage).
 *
 * @module batchFailure
 */

import { serverFailureText } from '@/shared/lib/serverFailure';
import { serverMessage } from '@/shared/lib/errorMessage';

/** What one failed photo adds to the batch's line under the count. */
export interface BatchFailureWords {
  /** The standard sentence (or the request limit's), once the failure was on our side; null otherwise. */
  ours: string | null;
  /** The refusal's own words (a photo too big in pixels, a photo the device could not make ready); null otherwise. */
  refused: string | null;
}

/**
 * The words for one photo that failed to go in: ours when the failure was on our side, else the refusal's own.
 *
 * @param error - What sending the photo threw.
 * @param deviceRefusal - The device's own sentence when the photo was never sent (it could not be made ready); null
 *   when it was sent.
 * @returns ours and refused, at most one of them set.
 */
export function batchFailureWords(error: unknown, deviceRefusal: string | null): BatchFailureWords {
  if (deviceRefusal !== null) return { ours: null, refused: deviceRefusal };
  const ours = serverFailureText(error, 'Tray upload');
  return { ours, refused: ours === null ? serverMessage(error) : null };
}
